import React, { useEffect, useState } from 'react';
import {
  Loader2, Film, Play, RefreshCw, CheckCircle, AlertCircle, Clock,
  Users, Search, Coins, X,
} from 'lucide-react';
import {
  getAdminStagingAnimations,
  getAdminStagingAnimationVideoUrl,
  type AdminStagingAnimation,
} from '../../services/supabase';

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-900/40 text-green-400',
  failed: 'bg-red-900/40 text-red-400',
  processing: 'bg-blue-900/40 text-blue-400',
  queued: 'bg-slate-700/60 text-slate-400',
};

const labelise = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function StagingAnimationsList() {
  const [animations, setAnimations] = useState<AdminStagingAnimation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ url: string; title: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setAnimations(await getAdminStagingAnimations());
    } catch (e) {
      console.error('Failed to load staging animations:', e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = animations.filter((a) => {
    if (statusFilter && a.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesEmail = a.owner_email?.toLowerCase().includes(q);
      const matchesName = a.project_name?.toLowerCase().includes(q);
      if (!matchesEmail && !matchesName) return false;
    }
    return true;
  });

  const stats = {
    total: animations.length,
    uniqueUsers: new Set(animations.map((a) => a.user_id)).size,
    completed: animations.filter((a) => a.status === 'completed').length,
    inProgress: animations.filter((a) => a.status === 'queued' || a.status === 'processing').length,
    failed: animations.filter((a) => a.status === 'failed').length,
    credits: animations
      .filter((a) => a.charged && !a.refunded)
      .reduce((sum, a) => sum + (a.credit_cost || 0), 0),
  };

  const openVideo = async (a: AdminStagingAnimation) => {
    setOpeningId(a.id);
    const url = await getAdminStagingAnimationVideoUrl(a);
    setOpeningId(null);
    if (url) setPlaying({ url, title: a.project_name || 'Staging Animation' });
  };

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 px-6 py-4 border-b border-slate-800">
        {[
          { label: 'Total', value: stats.total, icon: Film, color: 'text-slate-300' },
          { label: 'Users', value: stats.uniqueUsers, icon: Users, color: 'text-blue-400' },
          { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-green-400' },
          { label: 'In Progress', value: stats.inProgress, icon: Clock, color: 'text-amber-400' },
          { label: 'Failed', value: stats.failed, icon: AlertCircle, color: 'text-red-400' },
          { label: 'Credits', value: stats.credits, icon: Coins, color: 'text-purple-400' },
        ].map((s) => (
          <div key={s.label} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
            <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-800 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <button
          onClick={load}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <span className="text-sm text-slate-500">
          {filtered.length} animation{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 px-6">
            <Film className="w-12 h-12 mx-auto text-slate-700 mb-4" />
            <h4 className="text-slate-300 font-medium mb-1">No staging animations found</h4>
            <p className="text-sm text-slate-500">
              {searchQuery || statusFilter ? 'Try adjusting your filters' : 'Animations will appear here once users create them'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-900/80 text-slate-400 border-b border-slate-800 sticky top-0">
              <tr>
                <th className="px-4 py-3 font-medium">Before / After</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Owner Email</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Settings</th>
                <th className="px-4 py-3 font-medium">Credits</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-900/50">
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      {[a.before_image_url, a.after_image_url ?? a.thumbnail_url].map((src, i) => (
                        <div key={i} className="w-14 h-10 rounded overflow-hidden border border-slate-700 bg-slate-800">
                          {src && (
                            <img
                              src={src}
                              alt={i === 0 ? 'Before' : 'After'}
                              className="w-full h-full object-cover"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-slate-200 font-medium truncate max-w-[180px] block" title={a.project_name ?? undefined}>
                      {a.project_name || 'Untitled'}
                    </span>
                    {a.error_message && (
                      <p className="text-[10px] text-red-400/70 truncate max-w-[180px] mt-0.5" title={a.error_message}>
                        {a.error_message}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {a.owner_email ? (
                      <a
                        href={`mailto:${a.owner_email}`}
                        className="text-blue-400 hover:text-blue-300 hover:underline truncate max-w-[200px] block"
                        title={a.owner_email}
                      >
                        {a.owner_email}
                      </a>
                    ) : (
                      <span className="text-slate-600 italic">Unknown</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[a.status] ?? 'bg-slate-700/60 text-slate-400'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    <div className="text-slate-300">{labelise(a.style)}</div>
                    <div>{a.duration_seconds}s · {a.aspect_ratio} · {labelise(a.motion_level)}</div>
                    {a.model && (
                      <div className="text-[10px] text-slate-600 truncate max-w-[180px]" title={a.model}>{a.model}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                    <span className="text-slate-300">{a.credit_cost}</span>
                    {a.refunded ? (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-900/30 text-orange-400">Refunded</span>
                    ) : !a.charged ? (
                      <span className="ml-1.5 text-[10px] text-slate-600">Not charged</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {a.source === 'virtual_staging' ? 'Virtual Staging' : 'ImageMotion'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                    {new Date(a.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                    <br />
                    <span className="text-slate-600">
                      {new Date(a.created_at).toLocaleTimeString(undefined, { timeStyle: 'short' })}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {a.status === 'completed' && (a.output_url || a.output_storage_path) && (
                      <button
                        onClick={() => openVideo(a)}
                        disabled={openingId === a.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 disabled:opacity-50 text-xs font-medium"
                        title="Play video"
                      >
                        {openingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {playing && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setPlaying(null)}
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-200 font-medium truncate">{playing.title}</span>
              <div className="flex items-center gap-2">
                <a
                  href={playing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Open in new tab
                </a>
                <button onClick={() => setPlaying(null)} className="p-1 text-slate-400 hover:text-white" title="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <video src={playing.url} controls autoPlay className="w-full max-h-[75vh] rounded-xl bg-black" />
          </div>
        </div>
      )}
    </>
  );
}
